import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { APP_SCENARIOS, type AppScenario } from '@shared/lab/app-scenarios/index';
import { appResultTable } from '@shared/app-data-identity';
import { ToolResultPageRenderer } from '@/components/tools/ToolResultPageRenderer';
import { ForgeAppPreview } from '@/components/composer/ForgeAppPreview';
import { projectTestApp } from '@/lib/app-preview';
import { buildRunResultTabs, resultTabScopeSuffix } from '@/lib/tool-result-composition';
import { cn } from '@/lib/utils';
import { IN_SURFACE_CLASS } from '@/lib/views';

/**
 * Layout lab (development only, 02/10/2026): app scenarios built from fictional
 * data and rendered exactly like a real run (buildRunResultTabs →
 * ToolResultPageRenderer), to judge creation, freedom and visual consistency
 * without any model call. Scenarios live in shared/lab/app-scenarios and pass
 * the server's view schemas.
 */
// Designs proposed by the Forge's model on the lab's data
// (backend/scripts/layout-lab/propose.ts), shown after our scenarios.
const PROPOSED_SCENARIOS = Object.values(import.meta.glob<AppScenario>('@shared/lab/app-scenarios/proposed/*.json', { eager: true, import: 'default' }));
const LAB_SCENARIOS = [...APP_SCENARIOS, ...PROPOSED_SCENARIOS];

// U1: a scenario whose result is one list is also shown as the app the Forge previews.
const LAB_APP_DATA = {
  title: 'Liste', mode: 'rows' as const, object: 'company' as const,
  statuses: [{ key: 'todo', label: 'À traiter', done: false }, { key: 'in_progress', label: 'En cours', done: false }, { key: 'done', label: 'Fait', done: true }],
};

export const AppLabPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const scenario = LAB_SCENARIOS.find((item) => item.id === searchParams.get('scenario')) ?? LAB_SCENARIOS[0]!;
  const tabs = useMemo(() => buildRunResultTabs({ title: scenario.title, resultPayload: scenario.payload, fallback: null }), [scenario]);
  const activeTab = tabs.find((tab) => tab.id === searchParams.get('tab')) ?? tabs[0] ?? null;
  const asApp = useMemo(() => projectTestApp(LAB_APP_DATA, appResultTable(scenario.payload, null)), [scenario]);
  const select = (next: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(next)) {
      if (value === null) params.delete(key);
      else params.set(key, value);
    }
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-5 px-4 py-6 lg:flex-row">
      <nav aria-label="Scénarios" className="shrink-0 space-y-1 lg:w-64">
        <p className="px-2 text-xs font-medium uppercase tracking-wide text-domino-inkSoft">Banc d’essai — mise en page</p>
        {LAB_SCENARIOS.map((item) => (
          <button key={item.id} type="button" onClick={() => select({ scenario: item.id, tab: null })}
            aria-current={item.id === scenario.id ? 'page' : undefined}
            className={cn('block w-full rounded-lg px-2 py-1.5 text-left text-sm',
              item.id === scenario.id ? 'bg-domino-muted font-semibold text-domino-ink' : 'text-domino-inkSoft hover:text-domino-ink')}>
            {item.title}
          </button>
        ))}
      </nav>

      <main className="min-w-0 flex-1 space-y-4">
        <header className="space-y-2 rounded-2xl border border-domino-cardBorder bg-domino-white p-5">
          <h1 className="text-xl font-semibold text-domino-ink">{scenario.title}</h1>
          <p className="text-sm text-domino-inkSoft">{scenario.job}</p>
          <div className="grid gap-3 text-xs text-domino-inkSoft sm:grid-cols-2">
            <div><p className="font-medium text-domino-ink">Ce que le scénario éprouve</p><ul className="list-disc pl-4">{scenario.stresses.map((item) => <li key={item}>{item}</li>)}</ul></div>
            <div><p className="font-medium text-domino-ink">Ce qu’on attendrait et qui manque</p><ul className="list-disc pl-4">{scenario.wished.map((item) => <li key={item}>{item}</li>)}</ul></div>
          </div>
        </header>

        <section aria-label="Rendu" className={`overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white ${IN_SURFACE_CLASS}`}>
          {tabs.length > 1 && (
            <div role="tablist" aria-label="Perspectives du résultat" className="flex flex-wrap gap-1 border-b border-domino-cardBorder px-4">
              {tabs.map((tab) => (
                <button key={tab.id} type="button" role="tab" aria-selected={tab.id === activeTab?.id}
                  onClick={() => select({ tab: tab.id })}
                  className={cn('border-b-2 px-3 py-2 text-sm font-semibold transition',
                    tab.id === activeTab?.id ? 'border-domino-coral text-domino-ink' : 'border-transparent text-domino-inkSoft hover:text-domino-ink')}>
                  {tab.label}{resultTabScopeSuffix(tab.label, tab.scope)}
                </button>
              ))}
            </div>
          )}
          {activeTab
            ? <div className="pt-5"><ToolResultPageRenderer composition={activeTab.composition} viewEnvelopesByInstanceId={activeTab.viewEnvelopesByInstanceId}
                documentOutputs={activeTab.documentOutputs} pageMode="artifact" pageTitle={scenario.title} /></div>
            : <p className="p-5 text-sm text-domino-inkSoft">Aucun onglet.</p>}
        </section>

        {asApp && (
          <section aria-label="Comme l’app" className="rounded-2xl border border-domino-cardBorder bg-domino-white p-5">
            <ForgeAppPreview key={scenario.id} collection={asApp.collection} records={asApp.records} nextActions={null} />
          </section>
        )}
      </main>
    </div>
  );
};
